import React, { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

// Generic CRUD admin table for a resource.
// props: title, subtitle, resource(api), fields[], columns[], defaults{}

function ImageField({ value, onChange, uploadFn }) {
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Selecciona un archivo de imagen."); return; }
    setUploading(true);
    try {
      const url = await uploadFn(file);
      onChange(url);
      toast.success("Imagen subida");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <div
        data-testid="image-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-colors overflow-hidden group ${drag ? "border-[var(--gold)] bg-[var(--gold)]/5" : "border-black/15 hover:border-[var(--gold)]"}`}
      >
        {value ? (
          <div className="relative h-40">
            <img src={value} alt="preview" className="w-full h-40 object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
              <span className="text-white text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity">Cambiar imagen</span>
            </div>
          </div>
        ) : (
          <div className="h-40 flex flex-col items-center justify-center text-[#6B7688] gap-1 px-4 text-center">
            <ImageIcon className="w-8 h-8" />
            <span className="text-sm font-bold text-[#0A1C30]">Arrastra una foto aquí o haz clic para subir</span>
            <span className="text-xs">JPG, PNG, WEBP o GIF · máx 8 MB</span>
          </div>
        )}
        {uploading && <div className="absolute inset-0 bg-white/70 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="image-input" onChange={(e) => handleFile(e.target.files?.[0])} />
      {value && <button type="button" onClick={() => onChange("")} className="mt-2 text-xs font-bold text-[#E23B3B] hover:underline">Quitar imagen</button>}
    </div>
  );
}

export default function ResourceAdmin({ title, subtitle, resource, fields, columns, defaults }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults || {});
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    setLoading(true);
    resource.list().then(setItems).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setEditing(null); setForm(defaults || {}); setOpen(true); };
  const openEdit = (item) => {
    const next = { ...item };
    fields.forEach((f) => {
      const empty = next[f.name] === undefined || next[f.name] === null || (f.type === "select" && next[f.name] === "");
      if (empty && defaults && defaults[f.name] !== undefined) next[f.name] = defaults[f.name];
    });
    setEditing(item); setForm(next); setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const missing = fields.find((f) => f.required && f.type === "multiselect" && !(form[f.name] || []).length);
    if (missing) { toast.error(`Elige al menos una opción en «${missing.label}».`); return; }
    try {
      if (editing) {
        await resource.update(editing.id, form);
        toast.success("Actualizado correctamente");
      } else {
        await resource.create(form);
        toast.success("Creado correctamente");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmDelete = async () => {
    try {
      await resource.remove(toDelete.id);
      toast.success("Eliminado");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0A1C30]">{title}</h1>
          {subtitle && <p className="text-[#6B7688] mt-1">{subtitle}</p>}
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-5 py-3 transition-colors">
          <Plus className="w-5 h-5" /> Añadir
        </button>
      </div>

      <div className="mt-8 bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-black/5 bg-[#FAFAFA]">
                {columns.map((c) => (
                  <th key={c.name} className="px-5 py-3.5 text-xs font-extrabold tracking-wide text-[#6B7688] uppercase">{c.label}</th>
                ))}
                <th className="px-5 py-3.5 text-right text-xs font-extrabold tracking-wide text-[#6B7688] uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={columns.length + 1} className="px-5 py-10 text-center text-[#6B7688]">Cargando...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={columns.length + 1} className="px-5 py-10 text-center text-[#6B7688]">Sin registros aún.</td></tr>}
              {items.map((item) => (
                <tr key={item.id} className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]">
                  {columns.map((c) => (
                    <td key={c.name} className="px-5 py-4 text-sm text-[#2A3446]">
                      {c.render ? c.render(item) : String(item[c.name] ?? "—")}
                    </td>
                  ))}
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(item)} className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-[#EDF0F4] text-[#3B7DF6] transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setToDelete(item)} className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-[#FCE4E4] text-[#E23B3B] transition-colors ml-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-[#0A1C30]">{editing ? "Editar" : "Añadir"} {title.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((f) => (
              <div key={f.name}>
                <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} />
                ) : f.type === "image" ? (
                  <ImageField value={form[f.name] ?? ""} onChange={(v) => set(f.name, v)} uploadFn={f.uploadFn} />
                ) : f.type === "multiselect" ? (
                  (() => {
                    const raw = form[f.name];
                    const arr = Array.isArray(raw) && raw.length ? raw
                      : (f.legacyKey && form[f.legacyKey] ? [form[f.legacyKey]] : (Array.isArray(raw) ? raw : []));
                    const toggle = (val) => {
                      const cur = Array.isArray(form[f.name]) ? form[f.name] : arr;
                      const base = val === "Todos" ? [] : cur.filter((x) => x !== "Todos");
                      const next = cur.includes(val) ? base.filter((x) => x !== val) : (val === "Todos" ? ["Todos"] : [...base, val]);
                      set(f.name, next);
                    };
                    return (
                      <div className="rounded-xl ring-1 ring-black/10 p-3 max-h-56 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid={`multiselect-${f.name}`}>
                        {f.options.map((o) => (
                          <label key={o.value} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${arr.includes(o.value) ? "bg-[#FBF7E8]" : "hover:bg-[#FAFAFA]"}`}>
                            <input type="checkbox" checked={arr.includes(o.value)} onChange={() => toggle(o.value)} className="accent-[#E9A400] w-4 h-4" />
                            <span className="text-[#2A3446]">{o.label}</span>
                          </label>
                        ))}
                      </div>
                    );
                  })()
                ) : f.type === "select" ? (
                  <Select value={(form[f.name] ?? "") === "" ? "__none__" : form[f.name]} onValueChange={(v) => set(f.name, v === "__none__" ? "" : v)}>
                    <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder={f.placeholder || "Selecciona"} /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o.value} value={o.value === "" ? "__none__" : o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "switch" ? (
                  <div className="flex items-center gap-3 pt-1">
                    <Switch checked={!!form[f.name]} onCheckedChange={(v) => set(f.name, v)} />
                    <span className="text-sm text-[#6B7688]">{form[f.name] ? "Activo" : "Inactivo"}</span>
                  </div>
                ) : f.type === "date" || f.type === "time" ? (
                  <Input
                    type={f.type}
                    value={form[f.name] ?? ""}
                    onChange={(e) => set(f.name, e.target.value)}
                  />
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : "text"}
                    value={form[f.name] ?? ""}
                    onChange={(e) => set(f.name, f.type === "number" ? Number(e.target.value) : e.target.value)}
                    placeholder={f.placeholder}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-xl px-5 py-2.5 font-bold text-[#6B7688] hover:bg-[#EDF0F4] transition-colors">Cancelar</button>
            <button onClick={save} className="rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-6 py-2.5 transition-colors">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar registro?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#E23B3B] hover:bg-[#c72f2f]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
